import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { getQBStatus, connectQB, disconnectQB, syncQB, getGCalStatus, connectGCal, disconnectGCal, syncGCal, syncAll, getSyncStatus, getDigestStatus, sendDigest, previewDigest, testDigest, getTwilioStatus, configureTwilioWebhook } from "@/lib/api";
import { usePreferences } from "@/hooks/usePreferences";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link2, Unlink, RefreshCw, Download, Check, X, Calendar, FileSpreadsheet, MessageSquare, Clock, Mail, Send, Eye, List, LayoutGrid } from "lucide-react";

function StatusBadge({ testId, connected }) {
  return (
    <Badge
      data-testid={`${testId}-status`}
      variant="secondary"
      className={connected ? "bg-emerald-500/10 text-emerald-400" : "bg-zinc-500/10 text-zinc-400"}
    >
      {connected ? <><Check className="w-3 h-3 mr-1" /> Connected</> : <><X className="w-3 h-3 mr-1" /> Not Connected</>}
    </Badge>
  );
}

function LastSync({ lastSync }) {
  if (!lastSync) return null;
  return (
    <span className="text-xs text-muted-foreground/60 flex items-center gap-1">
      <Clock className="w-3 h-3" />
      Last sync: {new Date(lastSync).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
    </span>
  );
}

function IntegrationActions({ testId, connected, loading, onConnect, onDisconnect, children }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {!connected ? (
        <Button data-testid={`${testId}-connect-btn`} onClick={onConnect} disabled={loading} size="sm">
          <Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect
        </Button>
      ) : (
        <>
          {children}
          <Button data-testid={`${testId}-disconnect-btn`} variant="destructive" size="sm" onClick={onDisconnect} disabled={loading}>
            <Unlink className="w-3.5 h-3.5 mr-1.5" /> Disconnect
          </Button>
        </>
      )}
    </div>
  );
}

function IntegrationCard({ title, description, icon: Icon, connected, lastSync, loading, onConnect, onDisconnect, children, testId, view = "list" }) {
  if (view === "tiles") {
    // Finder-style tile: icon on top, actions pinned to the bottom.
    return (
      <Card data-testid={testId} className="border-border flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="w-12 h-12 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <StatusBadge testId={testId} connected={connected} />
          </div>
          <CardTitle className="text-base mt-3">{title}</CardTitle>
          <p className="text-xs text-muted-foreground">{description}</p>
          <LastSync lastSync={lastSync} />
        </CardHeader>
        <CardContent className="mt-auto">
          <IntegrationActions testId={testId} connected={connected} loading={loading} onConnect={onConnect} onDisconnect={onDisconnect}>
            {children}
          </IntegrationActions>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid={testId} className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">{title}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge testId={testId} connected={connected} />
            <LastSync lastSync={lastSync} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <IntegrationActions testId={testId} connected={connected} loading={loading} onConnect={onConnect} onDisconnect={onDisconnect}>
          {children}
        </IntegrationActions>
      </CardContent>
    </Card>
  );
}

export default function IntegrationsPage() {
  const { role } = useOutletContext();
  const [searchParams] = useSearchParams();
  const isOwner = role === "owner";

  const { prefs, setPref } = usePreferences();
  const view = prefs.integrations_view === "tiles" ? "tiles" : "list";

  const [qbStatus, setQBStatus] = useState(null);
  const [gcalStatus, setGCalStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState({ qb: false, gcal: false, qbSync: false, gcalSync: false, syncAll: false });

  const fetchStatuses = useCallback(async () => {
    try {
      const [qbRes, gcalRes, syncRes] = await Promise.all([getQBStatus(), getGCalStatus(), getSyncStatus()]);
      setQBStatus(qbRes.data);
      setGCalStatus(gcalRes.data);
      setSyncStatus(syncRes.data);
    } catch (err) { toast.error("Failed to refresh integrations status"); }
  }, []);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  // Handle OAuth return status
  useEffect(() => {
    const qbS = searchParams.get("qb_status");
    const gcalS = searchParams.get("gcal_status");
    if (qbS === "connected") toast.success("QuickBooks connected successfully!");
    if (qbS === "error") toast.error("QuickBooks connection failed. Please try again.");
    if (gcalS === "connected") toast.success("Google Calendar connected successfully!");
    if (gcalS === "error") toast.error("Google Calendar connection failed. Please try again.");
    if (qbS || gcalS) fetchStatuses();
  }, [searchParams, fetchStatuses]);

  const handleQBConnect = async () => {
    setLoading(l => ({ ...l, qb: true }));
    try {
      const res = await connectQB();
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to connect QuickBooks");
      setLoading(l => ({ ...l, qb: false }));
    }
  };

  const handleQBDisconnect = async () => {
    setLoading(l => ({ ...l, qb: true }));
    try {
      await disconnectQB();
      setQBStatus({ connected: false });
      toast.success("QuickBooks disconnected");
    } catch { toast.error("Failed to disconnect"); }
    finally { setLoading(l => ({ ...l, qb: false })); }
  };

  const handleQBSync = async () => {
    setLoading(l => ({ ...l, qbSync: true }));
    try {
      const res = await syncQB();
      const { imported, skipped, matched } = res.data;
      const parts = [];
      if (imported > 0) parts.push(`${imported} new estimates imported`);
      if (matched > 0) parts.push(`${matched} matched with calendar events`);
      if (skipped > 0) parts.push(`${skipped} already existed`);
      toast.success(parts.join(", ") || "QuickBooks in sync — no new estimates");
      fetchStatuses();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "QB sync failed");
    } finally { setLoading(l => ({ ...l, qbSync: false })); }
  };

  const handleGCalConnect = async () => {
    setLoading(l => ({ ...l, gcal: true }));
    try {
      const res = await connectGCal();
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to connect Google Calendar");
      setLoading(l => ({ ...l, gcal: false }));
    }
  };

  const handleGCalDisconnect = async () => {
    setLoading(l => ({ ...l, gcal: true }));
    try {
      await disconnectGCal();
      setGCalStatus({ connected: false });
      toast.success("Google Calendar disconnected");
    } catch { toast.error("Failed to disconnect"); }
    finally { setLoading(l => ({ ...l, gcal: false })); }
  };

  const handleGCalSync = async () => {
    setLoading(l => ({ ...l, gcalSync: true }));
    try {
      const res = await syncGCal();
      const { imported, skipped } = res.data;
      toast.success(imported > 0 ? `${imported} calendar events imported to Pending` : "Google Calendar in sync — no new events");
      fetchStatuses();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "GCal sync failed");
    } finally { setLoading(l => ({ ...l, gcalSync: false })); }
  };

  const handleSyncAll = async () => {
    setLoading(l => ({ ...l, syncAll: true }));
    try {
      const res = await syncAll();
      const gcal = res.data.google_calendar;
      const qb = res.data.quickbooks;
      const parts = [];
      if (gcal?.imported > 0) parts.push(`${gcal.imported} calendar events`);
      if (qb?.imported > 0) parts.push(`${qb.imported} QB estimates`);
      if (qb?.matched > 0) parts.push(`${qb.matched} auto-matched`);
      toast.success(parts.length > 0 ? `Synced: ${parts.join(", ")}` : "Everything is in sync — no new data");
      fetchStatuses();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setLoading(l => ({ ...l, syncAll: false })); }
  };

  return (
    <div data-testid="integrations-page" className={`space-y-6 ${view === "tiles" ? "max-w-5xl" : "max-w-3xl"}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Integrations</h1>
          <p className="text-muted-foreground text-sm mt-1">Connect external services to sync data into Production Hub</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-sm border border-border overflow-hidden">
            <Button data-testid="integrations-view-list" variant="ghost" size="icon"
              className={`h-8 w-8 rounded-none ${view === "list" ? "bg-secondary text-foreground" : "text-muted-foreground"}`}
              onClick={() => setPref("integrations_view", "list")} title="List view">
              <List className="w-4 h-4" />
            </Button>
            <Button data-testid="integrations-view-tiles" variant="ghost" size="icon"
              className={`h-8 w-8 rounded-none ${view === "tiles" ? "bg-secondary text-foreground" : "text-muted-foreground"}`}
              onClick={() => setPref("integrations_view", "tiles")} title="Tile view">
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          {(qbStatus?.connected || gcalStatus?.connected) && (
            <Button data-testid="sync-all-btn" onClick={handleSyncAll} disabled={loading.syncAll} size="sm" className="gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading.syncAll ? 'animate-spin' : ''}`} />
              {loading.syncAll ? "Syncing..." : "Sync All"}
            </Button>
          )}
        </div>
      </div>

      {syncStatus && (
        <Card className="border-border bg-secondary/5">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Auto-sync runs every <strong>15 minutes</strong></span>
              {syncStatus.google_calendar?.last_sync && (
                <span>GCal: {new Date(syncStatus.google_calendar.last_sync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              )}
              {syncStatus.quickbooks?.last_sync && (
                <span>QB: {new Date(syncStatus.quickbooks.last_sync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className={view === "tiles" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-6"}>
        <IntegrationCard
          testId="gcal-integration"
          title="Google Calendar"
          description="Pull events from your Google Calendar into Production Hub"
          icon={Calendar}
          connected={gcalStatus?.connected}
          lastSync={gcalStatus?.last_sync}
          loading={loading.gcal}
          onConnect={handleGCalConnect}
          onDisconnect={handleGCalDisconnect}
          view={view}
        >
          <Button data-testid="gcal-sync-btn" size="sm" variant="secondary" onClick={handleGCalSync} disabled={loading.gcalSync}>
            <Download className={`w-3.5 h-3.5 mr-1.5 ${loading.gcalSync ? 'animate-spin' : ''}`} />
            {loading.gcalSync ? "Pulling..." : "Pull Events"}
          </Button>
        </IntegrationCard>

        <IntegrationCard
          testId="qb-integration"
          title="QuickBooks Online"
          description="Pull new estimates from QuickBooks into Pending events"
          icon={FileSpreadsheet}
          connected={qbStatus?.connected}
          lastSync={qbStatus?.last_sync}
          loading={loading.qb}
          onConnect={handleQBConnect}
          onDisconnect={handleQBDisconnect}
          view={view}
        >
          <Button data-testid="qb-sync-btn" size="sm" variant="secondary" onClick={handleQBSync} disabled={loading.qbSync}>
            <Download className={`w-3.5 h-3.5 mr-1.5 ${loading.qbSync ? 'animate-spin' : ''}`} />
            {loading.qbSync ? "Pulling..." : "Pull Estimates"}
          </Button>
        </IntegrationCard>

        <TwilioCard isOwner={isOwner} view={view} />
      </div>

      {!isOwner && (
        <p className="text-xs text-muted-foreground/60 mt-4">Only owners can connect or disconnect integrations</p>
      )}

      {isOwner && <DigestSection />}
    </div>
  );
}


function TwilioCard({ isOwner, view }) {
  const [status, setStatus] = useState(null);
  const [configuring, setConfiguring] = useState(false);

  useEffect(() => {
    if (isOwner) {
      getTwilioStatus().then(r => setStatus(r.data)).catch(() => {});
    }
  }, [isOwner]);

  const handleConfigureWebhook = async () => {
    setConfiguring(true);
    try {
      const baseUrl = window.location.origin;
      await configureTwilioWebhook(baseUrl);
      const res = await getTwilioStatus();
      setStatus(res.data);
      toast.success("Twilio webhook configured — inbound replies will now update the dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to configure webhook");
    } finally { setConfiguring(false); }
  };

  const connected = status?.connected ?? false;
  const webhookOk = status?.webhook_configured ?? false;

  return (
    <IntegrationCard
      testId="twilio-integration"
      title="Twilio SMS"
      description="Send crew availability checks via text message"
      icon={MessageSquare}
      connected={connected}
      loading={false}
      onConnect={() => {}}
      onDisconnect={() => {}}
      view={view}
    >
      {connected && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs bg-emerald-500/10 text-emerald-400">
              {status?.phone_number || "Active"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {webhookOk ? (
              <Badge data-testid="twilio-webhook-status" variant="secondary" className="text-xs bg-emerald-500/10 text-emerald-400">
                <Check className="w-3 h-3 mr-1" />Inbound replies active
              </Badge>
            ) : (
              <>
                <Badge data-testid="twilio-webhook-status" variant="secondary" className="text-xs bg-amber-500/10 text-amber-400">
                  <X className="w-3 h-3 mr-1" />Inbound replies not configured
                </Badge>
                {isOwner && (
                  <Button data-testid="twilio-configure-webhook-btn" size="sm" variant="secondary" onClick={handleConfigureWebhook} disabled={configuring} className="text-xs h-7">
                    {configuring ? "Configuring..." : "Enable Replies"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {!connected && <Badge variant="secondary" className="text-xs">Not configured</Badge>}
    </IntegrationCard>
  );
}



function DigestSection() {
  const [digestStatus, setDigestStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testType, setTestType] = useState("owner");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    getDigestStatus().then(r => setDigestStatus(r.data)).catch(() => {});
  }, []);

  const handleSendAll = async () => {
    setSending(true);
    try {
      const res = await sendDigest();
      toast.success(`Digest emails sent to ${res.data.sent} recipients`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send digests");
    } finally { setSending(false); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error("Enter an email address"); return; }
    setSending(true);
    try {
      const res = await testDigest(testEmail, testType);
      toast.success(`Test digest sent to ${res.data.to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send test");
    } finally { setSending(false); }
  };

  const handlePreview = async (type) => {
    try {
      const res = await previewDigest(type);
      setPreviewHtml(res.data.html);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load preview");
    }
  };

  return (
    <>
      <Card data-testid="digest-integration" className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Weekly Digest Emails</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Automated weekly summaries sent every Sunday evening</p>
              </div>
            </div>
            <Badge
              data-testid="digest-status"
              variant="secondary"
              className={digestStatus?.configured ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}
            >
              {digestStatus?.configured ? <><Check className="w-3 h-3 mr-1" /> Configured</> : <><X className="w-3 h-3 mr-1" /> API Key Needed</>}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-secondary/30 rounded-md p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Digest Types</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="border border-border rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Owner Digest</span>
                  <Badge variant="outline" className="text-xs border-primary/30 text-primary">Full P&L</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Financial summary, upcoming events, crew availability, gear shortages, conflicts</p>
                <Button data-testid="preview-owner-digest" variant="ghost" size="sm" className="mt-2 h-7 text-xs" onClick={() => handlePreview("owner")}><Eye className="w-3 h-3 mr-1" /> Preview</Button>
              </div>
              <div className="border border-border rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Manager Digest</span>
                  <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400">No Revenue</Badge>
                </div>
                <p className="text-xs text-muted-foreground">Upcoming events, crew availability, gear shortages, conflicts — no P&L numbers</p>
                <Button data-testid="preview-manager-digest" variant="ghost" size="sm" className="mt-2 h-7 text-xs" onClick={() => handlePreview("manager")}><Eye className="w-3 h-3 mr-1" /> Preview</Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Send Test Email</label>
              <div className="flex gap-2">
                <Input data-testid="digest-test-email" placeholder="email@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} className="h-9 text-sm bg-secondary/30 border-border" />
                <select data-testid="digest-test-type" value={testType} onChange={e => setTestType(e.target.value)} className="h-9 px-3 rounded-sm border border-border bg-secondary/30 text-sm text-foreground">
                  <option value="owner">Owner</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
            </div>
            <Button data-testid="send-test-digest" size="sm" variant="secondary" onClick={handleTest} disabled={sending || !digestStatus?.configured} className="h-9">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Send Test
            </Button>
            <Button data-testid="send-all-digest" size="sm" onClick={handleSendAll} disabled={sending || !digestStatus?.configured} className="h-9 bg-primary text-primary-foreground hover:bg-primary/90">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending..." : "Send All Now"}
            </Button>
          </div>

          {!digestStatus?.configured && (
            <p className="text-xs text-amber-400 mt-2">Add your Resend API key (<code className="bg-secondary/50 px-1 rounded text-xs">RESEND_API_KEY</code>) to the backend .env file to enable email delivery.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto bg-card border-border">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Digest Email Preview</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">This is how the email will appear in recipient inboxes</DialogDescription>
          </DialogHeader>
          {previewHtml && (
            <div className="border border-border rounded-md overflow-hidden">
              <iframe
                data-testid="digest-preview-iframe"
                title="Digest Preview"
                srcDoc={previewHtml}
                className="w-full border-0"
                style={{ height: "600px", background: "#09090b" }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
